import { ref, computed, shallowRef, Ref } from "vue";
import {
  Address,
  createPublicClient,
  createWalletClient,
  custom,
  encodePacked,
  formatEther,
  formatUnits,
  hexToBigInt,
  keccak256,
  parseEther,
  parseUnits,
  type PublicClient,
  type WalletClient,
} from "viem";
import { polygon } from "viem/chains";
import { defineStore } from "pinia";
import {
  Web3AuthNoModal,
  WEB3AUTH_NETWORK,
  WALLET_CONNECTORS,
  AUTH_CONNECTION,
  AuthUserInfo,
  type Web3AuthNoModalOptions,
} from "@web3auth/modal";
import {
  ADAPTER_EVENTS,
  ADAPTER_STATUS,
  CHAIN_NAMESPACES,
  WEB3AUTH_NETWORK_TYPE,
} from "@web3auth/base";
import {
  wmaticContractMainnet,
  wmaticContractMumbai,
  isProd,
  seaportContractMainnet,
  seaportContractMumbai,
  usdcContractMainnet,
  seaportConduitKey,
  seaportConf,
} from "@/application/config";
import { ethers, providers } from "ethers";
import { SeaportService } from "~/services/seaportService";
import { SiweMessage } from "siwe";
import { getAuthVersion, setAuthVersion, userToken } from "~/utils/localStorage";
import { auctionApi } from "~/api/auction";
import { useStoreApp } from "./storeApp";

import { useStoreUserV2 } from "./storeUserV2";
import { http } from "viem";
import { MarketType } from "fungi-types";
import { Currency } from "fungi-types";
import axios from "axios";

// AA SDK
import { WalletClientSigner, SmartAccountClient, EthersProviderAdapter } from "@aa-sdk/core";
import { alchemy, polygon as polygonAlchemy } from "@account-kit/infra";
import { createModularAccountV2Client, ModularAccountV2 } from "@account-kit/smart-contracts";
import { FUNGIBALL_V1 } from "fungi-utils/salts";
import { Seaport } from "@opensea/seaport-js";

export const SECONDARY_EXECUTOR_ADDRESS: Address = "0xA27096eEcD6EE48691a0852Ff9853df5bf18F39A";
export const PRIMARY_EXECUTOR_ADDRESS: Address = "0xE4281FEE3C9c9843B44c15D37bc7D160dF0312Be";

const PARASWAP_API = "https://apiv5.paraswap.io";

const ALCHEMY_RPC = import.meta.env.VITE_ALCHEMY_POLYGON_RPC_URL;

type AdapterStatus = (typeof ADAPTER_STATUS)[keyof typeof ADAPTER_STATUS];

// Option 1: Only listen to the events that are actually supported
const supportedEvents = [
  ADAPTER_EVENTS.CONNECTED,
  ADAPTER_EVENTS.CONNECTING,
  ADAPTER_EVENTS.DISCONNECTED,
  ADAPTER_EVENTS.ERRORED,
  ADAPTER_EVENTS.NOT_READY,
  ADAPTER_EVENTS.READY,
  ADAPTER_EVENTS.CACHE_CLEAR,
] as const;

// WPOL Contract Configuration
const WPOL_CONTRACT_ADDRESS = isProd
  ? (wmaticContractMainnet as Address)
  : (wmaticContractMumbai as Address);
const USDC_CONTRACT_ADDRESS: Address = usdcContractMainnet;
const SEAPORT_CONTRACT_ADDRESS: Address = isProd
  ? (seaportContractMainnet as Address)
  : (seaportContractMumbai as Address);
const SEAPORT_CONDUIT_ADDRESS: Address = "0x0dF75B43766E622155c744Aae6ed8a9D6574a9ae";

const SEAPORT_CONDUIT_KEY = seaportConduitKey;

// WPOL ABI (ERC-20 standard functions we need)
const WPOL_ABI = [
  {
    name: "balanceOf",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "account", type: "address" }],
    outputs: [{ name: "balance", type: "uint256" }],
  },
  {
    name: "allowance",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "owner", type: "address" },
      { name: "spender", type: "address" },
    ],
    outputs: [{ name: "remaining", type: "uint256" }],
  },
  {
    name: "approve",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "spender", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [{ name: "success", type: "bool" }],
  },
  {
    name: "decimals",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint8" }],
  },
  {
    name: "deposit",
    type: "function",
    stateMutability: "payable",
    inputs: [],
    outputs: [],
  },
  {
    name: "withdraw",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "amount", type: "uint256" }],
    outputs: [],
  },
  {
    name: "transfer",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "to", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [{ name: "success", type: "bool" }],
  },
] as const;

// USDC ABI (standard ERC-20)
const USDC_ABI = [
  {
    name: "balanceOf",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "account", type: "address" }],
    outputs: [{ name: "balance", type: "uint256" }],
  },
  {
    name: "allowance",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "owner", type: "address" },
      { name: "spender", type: "address" },
    ],
    outputs: [{ name: "remaining", type: "uint256" }],
  },
  {
    name: "approve",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "spender", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [{ name: "success", type: "bool" }],
  },
  {
    name: "decimals",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint8" }],
  },
  {
    name: "transfer",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "to", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [{ name: "success", type: "bool" }],
  },
  {
    name: "transferFrom",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "from", type: "address" },
      { name: "to", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [{ name: "success", type: "bool" }],
  },
] as const;

// Allowance cache interface
interface AllowanceCache {
  amount: number;
  timestamp: number;
  walletAddress: string;
}

const QUICKSWAP_ROUTER_ADDRESS: Address = "0xa5E0829CaCEd8fFDD4De3c43696c57F7D7A678ff";
// ABI du Router QuickSwap avec la fonction pour POL natif
const QUICKSWAP_ROUTER_ABI = [
  {
    name: "swapExactETHForTokens",
    type: "function",
    stateMutability: "payable",
    inputs: [
      { name: "amountOutMin", type: "uint256" },
      { name: "path", type: "address[]" },
      { name: "to", type: "address" },
      { name: "deadline", type: "uint256" },
    ],
    outputs: [{ name: "amounts", type: "uint256[]" }],
  },
  {
    name: "getAmountsOut",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "amountIn", type: "uint256" },
      { name: "path", type: "address[]" },
    ],
    outputs: [{ name: "amounts", type: "uint256[]" }],
  },
] as const;

const isInitializing = ref<boolean>(false);

export type ModularAccountClient = SmartAccountClient<any, any, ModularAccountV2>;

export const useWeb3AuthStore = defineStore("web3authStore", () => {
  // State
  const isConnecting = ref<boolean>(false);
  const error = ref<string | null>(null);
  const isConnected = ref<boolean>(false);
  const web3auth = shallowRef<Web3AuthNoModal | null>(null);
  const polygonReader = shallowRef<PublicClient | null>(null);
  const userWalletClient = shallowRef<WalletClient | null>(null);
  const userInfo = ref<Partial<AuthUserInfo> | null>(null);
  const isInitialized = ref<boolean>(false);
  const status = ref<AdapterStatus>(ADAPTER_STATUS.NOT_READY);
  const polBalance = ref<string>("0");
  const wpolBalance = ref<string>("0");
  const usdcBalance = ref<string>("0");
  const usdcBalanceSmartWallet = ref<string>("0");
  const walletAddress = ref<string>("");
  const polRawBalance = ref<string>("0");
  const wpolRawBalance = ref<string>("0");
  const usdcRawBalance = ref<string>("0");
  const usdcRawBalanceSmartWallet = ref<string>("0");
  const web3AuthNetwork = ref<WEB3AUTH_NETWORK_TYPE>(WEB3AUTH_NETWORK.SAPPHIRE_MAINNET);

  // Smart Wallet
  const smartWalletClient = shallowRef<ModularAccountClient | null>(null);
  const smartWalletAddress = computed(() => smartWalletClient.value?.getAddress() || null);

  // Allowance cache
  const allowanceCache = ref<AllowanceCache | null>(null);
  const allowanceUsdcCache = ref<AllowanceCache | null>(null);
  const CACHE_TTL_MS = 30_000; // 30 seconds

  // Ethers.js provider and signer
  const ethersProvider = shallowRef<ethers.providers.Web3Provider | null>(null);
  const ethersSigner = shallowRef<ethers.Signer | null>(null);

  // Getters
  const provider = computed(() => web3auth.value?.provider);
  const isOnLegacyNetwork = computed(() => web3AuthNetwork.value === WEB3AUTH_NETWORK.CYAN);
  const connectionType = computed(() => web3auth.value?.connectedConnectorName);

  // Searport Service
  const seaportService = computed(() => {
    if (!ethersSigner.value) return null;

    try {
      return new SeaportService(ethersSigner.value);
    } catch (error) {
      console.error("Failed to initialize SeaportService:", error);
      return null;
    }
  });

  const seaportSmartWalletService = computed(() => {
    if (!smartWalletClient.value) return null;
    try {
      const signer = getSmartWalletEthersSigner(smartWalletClient.value);
      return new SeaportService(signer);
    } catch (error) {
      console.error("Failed to initialize SeaportService for Smart Wallet:", error);
      return null;
    }
  });

  // Helper: Format balance for display
  const formatBalance = (balance: string) => {
    const num = parseFloat(balance);
    const roundedDown = Math.floor(num * 100) / 100;
    return roundedDown.toFixed(2).replace(/\.?0+$/, "");
  };

  // Initialize Viem clients
  const initializeClients = () => {
    if (!provider.value) return;

    // Alchemy for read only (performance+++)
    polygonReader.value = createPublicClient({
      chain: polygon,
      transport: http(ALCHEMY_RPC),
    });

    // Web3Auth for transactions/signatures
    userWalletClient.value = createWalletClient({
      chain: polygon,
      transport: custom(provider.value),
    });

    // Ethers provider and signer
    ethersProvider.value = new ethers.providers.Web3Provider(provider.value);
    ethersSigner.value = ethersProvider.value.getSigner();
  };

  // Sign Message
  const signMessage = async (input: { message: string; walletAddress: `0x${string}` }) => {
    if (!userWalletClient.value) {
      throw new Error("Wallet client not available");
    }
    try {
      const [account] = await userWalletClient.value.getAddresses();
      console.log("account", account);
      const signedMessage = await userWalletClient.value.signMessage({
        message: input.message,
        account: input.walletAddress,
      });
      return signedMessage;
    } catch (err: any) {
      throw new Error(`Sign message error: ${err.message}`);
    }
  };

  // Initialize Web3Auth
  const initialize = async (legacyNetwork: boolean = true) => {
    const userStore = useStoreUserV2();
    // isInitializing.value = true;
    console.log("🔄 Initializing Web3Auth...");

    // ✅ Vérification robuste
    if (isInitialized.value) {
      console.log("Web3Auth already initialized, skipping");
      return;
    }

    // ✅ Protection contre les appels concurrents
    if (isInitializing.value) {
      console.log("Web3Auth initialization already in progress, skipping");
      return;
    }

    try {
      const authVersion = getAuthVersion();
      error.value = null;

      type NetworkSwitch = {
        clientId: string;
        web3AuthNetwork: WEB3AUTH_NETWORK_TYPE;
      };
      const netWorkSwitch: {
        legacy: NetworkSwitch;
        new: NetworkSwitch;
      } = {
        legacy: {
          clientId:
            "BK3iWzX618_NEN0sZV-Zhmmx-OKqUNM_dq4XQhBsRAfnCBoGwYGxWhsJWkjYE1mXzYWla4TMXoZBiviGkiKaBVk",
          web3AuthNetwork: WEB3AUTH_NETWORK.CYAN,
        },
        new: {
          clientId:
            "BIT1x2agnykSVlREfLlpq1WU3gYpQQ6iTXE7dUomkp8TAE2DQ2qHvsEc4T3wIIYI-SkkhPdSM5PorJqzySi9SbA",
          web3AuthNetwork: WEB3AUTH_NETWORK.SAPPHIRE_MAINNET,
        },
      };

      const network = authVersion === "v1" ? netWorkSwitch.legacy : netWorkSwitch.new;
      web3AuthNetwork.value = network.web3AuthNetwork;

      let web3AuthOptions: Web3AuthNoModalOptions = {
        clientId: network.clientId,
        web3AuthNetwork: network.web3AuthNetwork,
        defaultChainId: "0x89",
        sessionTime: 86400 * 7,
        storageType: "local",
        chains: [
          {
            chainNamespace: CHAIN_NAMESPACES.EIP155,
            chainId: "0x89", // Polygon Mainnet
            rpcTarget: ALCHEMY_RPC,
            displayName: "Polygon Mainnet",
            blockExplorerUrl: "https://polygonscan.com",
            ticker: "POL",
            tickerName: "Polygon",
            logo: "https://images.toruswallet.io/polygon.svg",
          },
        ],
        uiConfig: {
          uxMode: "redirect",
        },
      };

      web3auth.value = new Web3AuthNoModal(web3AuthOptions);

      // Listen to all events
      for (const event of supportedEvents) {
        web3auth.value?.on(event, async (...args: any[]) => {
          status.value = web3auth.value?.status as AdapterStatus;

          if (event === ADAPTER_EVENTS.CONNECTED) {
            isConnected.value = true;
            await loadWalletInfo();
            isInitialized.value = true;
          } else if (event !== ADAPTER_EVENTS.CONNECTING) {
            isInitialized.value = true;
          }

          if (event === ADAPTER_EVENTS.DISCONNECTED) {
            userInfo.value = null;
            allowanceCache.value = null;
            allowanceUsdcCache.value = null;
          }
        });
      }

      const loadWalletInfo = async () => {
        initializeClients();
        getPolBalance();
        getWpolBalance();
        getUsdcBalance();

        // Promise.all to speed up
        await Promise.all([getWalletAddress(), getUserInfo()]);

        // Initialize Smart Wallet
        await initSmartWallet();
        await userStore.login();
      };

      // Error event
      web3auth.value.on(ADAPTER_EVENTS.ERRORED, (err: any) => {
        console.error("Web3Auth error:", err);
        error.value = err.message;
      });

      // Connecting event
      web3auth.value.on(ADAPTER_EVENTS.CONNECTING, () => {
        isConnecting.value = true;
      });

      await web3auth.value.init();

      console.log("✅ Web3Auth initialized");
    } catch (err: any) {
      error.value = err.message;
      console.error("Web3Auth initialization error ???:", err.message);
      throw err;
    }
  };

  // Cleanup
  const cleanup = async () => {
    if (web3auth.value) {
      try {
        await logout();
        // Remove all event listeners
        web3auth.value.removeAllListeners();

        web3auth.value = null;
      } catch (err) {
        console.error("Cleanup error:", err);
      }
    }
  };

  const reboot = async () => {
    isInitialized.value = false;
    await cleanup();
    console.log("Re-initializing Web3Auth...");
    await initialize();
    console.log("✅ Web3Auth rebooted");
    // wait for status.value === ADAPTER_STATUS.READY
    return new Promise<void>((resolve, reject) => {
      const checkReady = setInterval(() => {
        console.log("Checking if Web3Auth is ready...");
        if (status.value === ADAPTER_STATUS.READY) {
          clearInterval(checkReady);
          clearTimeout(timeout);
          resolve();
        }
      }, 500);

      const timeout = setTimeout(() => {
        console.log("Reboot timed out");
        clearInterval(checkReady);
        reject(new Error("Reboot timed out after 10 seconds"));
      }, 20000);
    });
  };

  // Logout
  const logout = async () => {
    console.log("Logging out...", web3auth.value);
    if (!web3auth.value || web3auth.value?.status !== "connected") return;

    try {
      error.value = null;
      await web3auth.value.logout();
      isConnected.value = false;
      userInfo.value = null;
      allowanceCache.value = null; // Clear cache
      ethersSigner.value = null; // Clear signer
      allowanceUsdcCache.value = null;
      userToken.remove();
      console.log("Logged out successfully");
    } catch (err: any) {
      error.value = err.message;
      console.error("Logout error:", err);
      throw err;
    }
  };

  // Get wallet address
  const getWalletAddress = async () => {
    if (!userWalletClient.value) {
      throw new Error("Wallet client not available");
    }

    const addresses = await userWalletClient.value.getAddresses();
    walletAddress.value = addresses[0];
  };

  // Get native POL balance (for gas)
  const getPolBalance = async () => {
    if (!polygonReader.value || !userWalletClient.value) {
      throw new Error("Clients not available");
    }

    try {
      const [address] = await userWalletClient.value.getAddresses();
      const balance = await polygonReader.value.getBalance({ address });
      const resp = formatEther(balance);

      polBalance.value = resp;
      polRawBalance.value = balance.toString();
      return {
        raw: balance,
        full: resp,
        formatted: resp,
      };
    } catch (err: any) {
      error.value = err.message;
      throw err;
    }
  };

  const getUsdcBalance = async () => {
    if (!polygonReader.value || !userWalletClient.value) {
      throw new Error("Clients not available");
    }

    try {
      const [address] = await userWalletClient.value.getAddresses();

      const balance = await polygonReader.value.readContract({
        address: USDC_CONTRACT_ADDRESS,
        abi: USDC_ABI,
        functionName: "balanceOf",
        args: [address],
      });

      const resp = formatUnits(balance as bigint, 6); // USDC has 6 decimals
      usdcBalance.value = formatBalance(resp);
      usdcRawBalance.value = balance.toString();

      const balanceSmartWallet = await polygonReader.value.readContract({
        address: USDC_CONTRACT_ADDRESS,
        abi: USDC_ABI,
        functionName: "balanceOf",
        args: [smartWalletAddress.value as Address],
      });
      usdcBalanceSmartWallet.value = formatBalance(formatUnits(balanceSmartWallet as bigint, 6));
      usdcRawBalanceSmartWallet.value = balanceSmartWallet.toString();
      return {
        raw: balance,
        formatted: resp,
      };
    } catch (err: any) {
      error.value = err.message;
      throw err;
    }
  };

  // Get WPOL balance (for trading)
  const getWpolBalance = async () => {
    if (!polygonReader.value || !userWalletClient.value) {
      throw new Error("Clients not available");
    }

    try {
      const [address] = await userWalletClient.value.getAddresses();

      const balance = await polygonReader.value.readContract({
        address: WPOL_CONTRACT_ADDRESS,
        abi: WPOL_ABI,
        functionName: "balanceOf",
        args: [address],
      });

      const resp = formatUnits(balance as bigint, 18);
      wpolBalance.value = formatBalance(resp);
      wpolRawBalance.value = balance.toString();
      return {
        raw: balance,
        formatted: resp,
      };
    } catch (err: any) {
      error.value = err.message;
      throw err;
    }
  };

  // Get gas price from gas station
  const getGasPrice = async () => {
    if (!polygonReader.value) {
      throw new Error("Reader client not available");
    }

    try {
      const feeData = await polygonReader.value.estimateFeesPerGas();

      return {
        maxFeePerGas: feeData.maxFeePerGas,
        maxPriorityFeePerGas: feeData.maxPriorityFeePerGas,
      };
    } catch (err: any) {
      error.value = err.message;
      throw err;
    }
  };

  // Estimate gas for POL transfer
  const estimatePolTransferGas = async (): Promise<{
    gasLimit: bigint;
    gasPrice: bigint;
    gasFee: string;
    gasFeeNumber: number;
  }> => {
    if (!polygonReader.value) {
      throw new Error("Reader not available");
    }

    try {
      const gasLimit = 21000n; // Standard POL transfer gas
      const gasPrice = await getGasPrice();
      const gasFeeWei = gasPrice.maxFeePerGas * gasLimit;
      const gasFee = formatEther(gasFeeWei);

      return {
        gasLimit,
        gasPrice: gasPrice.maxFeePerGas,
        gasFee, // String: "0.000021"
        gasFeeNumber: parseFloat(gasFee), // Number: 0.000021
      };
    } catch (err: any) {
      console.error("Estimate POL gas error:", err);
      throw err;
    }
  };

  // Estimate gas for WPOL transfer
  const estimateWpolTransferGas = async ({
    toAddress,
    amount,
  }: {
    toAddress: string;
    amount: number;
  }): Promise<{
    gasLimit: bigint;
    gasPrice: bigint;
    gasFee: string;
    gasFeeNumber: number;
  }> => {
    if (!polygonReader.value || !walletAddress.value) {
      throw new Error("Clients not available");
    }

    try {
      // Validate address
      if (!toAddress.match(/^0x[a-fA-F0-9]{40}$/)) {
        throw new Error("Invalid recipient address format");
      }

      // Estimate gas for WPOL transfer
      const gasLimit = await polygonReader.value.estimateContractGas({
        address: WPOL_CONTRACT_ADDRESS,
        abi: WPOL_ABI,
        functionName: "transfer",
        args: [toAddress as Address, parseEther(amount.toString())],
        account: walletAddress.value as Address,
      });

      const gasPrice = await getGasPrice();
      const gasFeeWei = gasPrice.maxFeePerGas * gasLimit;
      const gasFee = formatEther(gasFeeWei);

      return {
        gasLimit,
        gasPrice: gasPrice.maxFeePerGas,
        gasFee, // String: "0.000045"
        gasFeeNumber: parseFloat(gasFee), // Number: 0.000045
      };
    } catch (err: any) {
      console.error("Estimate WPOL gas error:", err);
      throw err;
    }
  };

  // Fast gas estimation using fixed gas amounts (no network call for gas estimation)
  const estimateWrapUnWrapGasFast = async (
    direction: "wrap" | "unwrap" | "pol-to-wpol" | "wpol-to-pol"
  ): Promise<{
    gasEstimate: bigint;
    gasFeeInPOL: string;
    gasFeeInWei: bigint;
  }> => {
    const isWrap = direction === "wrap" || direction === "pol-to-wpol";

    // Fixed gas estimates for Polygon WPOL operations
    const WRAP_GAS = 50000n; // deposit() typically uses ~45-50k gas
    const UNWRAP_GAS = 35000n; // withdraw() typically uses ~30-35k gas

    const gasAmount = isWrap ? WRAP_GAS : UNWRAP_GAS;

    try {
      // Only fetch current gas price (fast operation)
      const gasFee = await calculateGasFee(gasAmount, 120);

      return {
        gasEstimate: gasAmount,
        gasFeeInPOL: formatEther(gasFee),
        gasFeeInWei: gasFee,
      };
    } catch (err: any) {
      error.value = err.message;
      console.error("Fast gas estimation error:", err);
      throw err;
    }
  };

  // Estimate gas for a contract call
  const estimateContractGas = async (params: {
    address: Address;
    abi: any;
    functionName: string;
    args?: any[];
    value?: bigint;
  }) => {
    if (!polygonReader.value || !walletAddress.value) {
      throw new Error("Clients not available");
    }

    try {
      const gasEstimate = await polygonReader.value.estimateContractGas({
        address: params.address,
        abi: params.abi,
        functionName: params.functionName,
        args: params.args ?? [],
        account: walletAddress.value as Address,
        value: params.value,
      });

      return gasEstimate;
    } catch (err: any) {
      error.value = err.message;
      throw err;
    }
  };

  // Calculate total gas fee with buffer
  const calculateGasFee = async (
    gasAmount: bigint,
    bufferPercent: number = 200 // 200% = 2x buffer
  ): Promise<bigint> => {
    const feeData = await getGasPrice();

    // Gas fee = gasAmount * maxFeePerGas * buffer
    const gasFee = (gasAmount * feeData.maxFeePerGas! * BigInt(bufferPercent)) / BigInt(100);

    return gasFee;
  };

  // Check if user has enough native POL for gas
  const hasEnoughGas = async (estimatedGasFee: bigint): Promise<boolean> => {
    if (!polygonReader.value || !walletAddress.value) {
      throw new Error("Clients not available");
    }

    const balance = await polygonReader.value.getBalance({
      address: walletAddress.value as Address,
    });

    return balance >= estimatedGasFee;
  };

  // Get Seaport allowance for WPOL
  const getAllowance = async (input: {
    market: MarketType.PRIMARY | MarketType.SECONDARY;
    currency: "wpol" | "usdc";
    tokenAddress?: Address;
  }): Promise<number> => {
    if (!polygonReader.value || !walletAddress.value) {
      throw new Error("Clients not available");
    }

    try {
      let tokenAddress: Address = USDC_CONTRACT_ADDRESS;
      if (input.currency === "usdc") tokenAddress = USDC_CONTRACT_ADDRESS;

      let spenderAddress: Address = SEAPORT_CONDUIT_ADDRESS;
      if (input.market === MarketType.SECONDARY) spenderAddress = SECONDARY_EXECUTOR_ADDRESS;

      console.log(
        `Fetching allowance for ${input.currency.toUpperCase()} on ${input.market} market`
      );
      console.log("Token address:", tokenAddress);
      console.log("Spender address:", spenderAddress);
      console.log("Wallet address:", walletAddress.value);
      const allowance = await polygonReader.value.readContract({
        address: tokenAddress,
        abi: input.currency === "wpol" ? WPOL_ABI : USDC_ABI,
        functionName: "allowance",
        args: [walletAddress.value as `0x${string}`, spenderAddress],
      });

      // FIX: Utiliser les bonnes décimales selon la currency
      const decimals = input.currency === "usdc" ? 6 : 18;
      return parseFloat(formatUnits(allowance as bigint, decimals));
    } catch (err: any) {
      console.error("Get allowance error:", err);
      error.value = err.message;
      throw err;
    }
  };

  // Approve WPOL for Seaport
  const approveAllowance = async (input: {
    amount: number;
    currency: "wpol" | "usdc";
    market: MarketType.PRIMARY | MarketType.SECONDARY;
  }): Promise<string> => {
    const { amount, currency, market } = input;
    if (!userWalletClient.value || !walletAddress.value) throw new Error("Clients not available");

    try {
      console.log(`Approving ${amount} ${currency.toUpperCase()} for Seaport`);
      let tokenAddress: Address = WPOL_CONTRACT_ADDRESS;
      if (currency === "usdc") tokenAddress = USDC_CONTRACT_ADDRESS;

      let spenderAddress: Address = SEAPORT_CONDUIT_ADDRESS;
      if (input.market === MarketType.SECONDARY) spenderAddress = SECONDARY_EXECUTOR_ADDRESS;

      const abi = currency === "usdc" ? USDC_ABI : WPOL_ABI;

      let finalAmount = parseEther(amount.toString());
      if (currency === "usdc") {
        finalAmount = parseUnits(amount.toString(), 6); // USDC has 6 decimals
      }

      const hash = await userWalletClient.value.writeContract({
        address: tokenAddress,
        abi: abi,
        chain: polygon,
        functionName: "approve" as const,
        args: [spenderAddress, finalAmount],
        account: walletAddress.value as `0x${string}`,
      });

      // Wait for transaction confirmation
      if (polygonReader.value) {
        await polygonReader.value.waitForTransactionReceipt({ hash });
      }

      console.log(`Approval successful. Tx hash: ${hash}`);
      return hash;
    } catch (err: any) {
      error.value = err.message;
      throw err;
    }
  };

  // Check if cache is valid
  const isCacheValid = (currency: "usdc" | "wpol"): boolean => {
    const cache = currency === "usdc" ? allowanceUsdcCache.value : allowanceCache.value;
    if (!cache) return false;

    const now = Date.now();
    const isAddressMatch = cache.walletAddress === walletAddress.value;
    const isNotExpired = now - cache.timestamp < CACHE_TTL_MS;

    return isAddressMatch && isNotExpired;
  };

  // Update cache
  const updateCache = (amount: number) => {
    allowanceCache.value = {
      amount,
      timestamp: Date.now(),
      walletAddress: walletAddress.value,
    };
  };

  // Ensure WPOL allowance with caching
  const ensureAllowance = async (input: {
    requiredAmount: number;
    currency: "wpol" | "usdc";
    market: MarketType.PRIMARY | MarketType.SECONDARY;
    forceRefresh?: boolean;
    confirmations?: number;
  }): Promise<{ success: boolean; allowance: number; txHash?: string }> => {
    const { requiredAmount, currency, market, forceRefresh = false, confirmations = 1 } = input;
    // const APPROVAL_BUFFER = 100_000; // Approve extra to reduce future transactions
    const minRequired = requiredAmount;
    const approvalAmount = requiredAmount;

    console.log(`Ensuring WPOL allowance: required=${requiredAmount}`);
    const allowanceCacheValue =
      currency === "usdc" ? allowanceUsdcCache.value : allowanceCache.value;

    try {
      // Check cache first
      if (!forceRefresh && isCacheValid(currency) && allowanceCacheValue!.amount >= minRequired) {
        return { success: true, allowance: allowanceCacheValue!.amount };
      }

      // Fetch current allowance from blockchain
      const currentAllowance = await getAllowance({
        market,
        currency,
      });

      // Update cache
      updateCache(currentAllowance);

      // Check if sufficient
      if (currentAllowance >= minRequired) {
        return { success: true, allowance: currentAllowance };
      }

      // Need to approve more
      const txHash = await approveAllowance({
        amount: approvalAmount,
        currency,
        market,
      });

      // Update cache with new allowance
      updateCache(approvalAmount);

      return {
        success: true,
        allowance: approvalAmount,
        txHash,
      };
    } catch (error: any) {
      console.error("Failed to approve WPOL:", error);

      // Handle user rejection
      if (error.code === 4001 || error.name === "UserRejectedRequestError") {
        throw new Error("User rejected the transaction");
      }

      return {
        success: false,
        allowance: allowanceCache.value?.amount ?? 0,
      };
    }
  };

  // Wrap POL to WPOL
  const wrapPol = async (amount: number): Promise<string> => {
    if (!userWalletClient.value || !walletAddress.value || !polygonReader.value) {
      throw new Error("Clients not available");
    }

    try {
      console.log(`Wrapping ${amount} POL to WPOL`);

      // Check if user has enough POL balance
      await getPolBalance();
      const polBalanceValue = parseFloat(polBalance.value);

      if (polBalanceValue < amount) {
        throw new Error(
          `Insufficient POL balance. You have ${polBalanceValue.toFixed(4)} POL but need ${amount} POL`
        );
      }

      // Call the deposit function with the POL amount as value
      const hash = await userWalletClient.value.writeContract({
        address: WPOL_CONTRACT_ADDRESS,
        abi: WPOL_ABI,
        chain: polygon,
        functionName: "deposit" as const,
        args: [],
        value: parseEther(amount.toString()),
        account: walletAddress.value as `0x${string}`,
      });

      // Wait for transaction confirmation
      await polygonReader.value.waitForTransactionReceipt({ hash });

      console.log(`Wrap successful. Tx hash: ${hash}`);

      // Refresh balances
      await getPolBalance();
      await getWpolBalance();
      await getUsdcBalance();

      return hash;
    } catch (err: any) {
      error.value = err.message;
      console.error("Wrap error:", err);
      throw err;
    }
  };

  // Unwrap WPOL to POL
  const unwrapWpol = async (amount: number): Promise<string> => {
    if (!userWalletClient.value || !walletAddress.value || !polygonReader.value) {
      throw new Error("Clients not available");
    }

    try {
      console.log(`Unwrapping ${amount} WPOL to POL`);
      console.log(`Unwrapping ${amount} WPOL to POL`);

      // Check if user has enough WPOL balance
      await getWpolBalance();
      const wpolBalanceValue = parseFloat(wpolBalance.value);

      if (wpolBalanceValue < amount) {
        throw new Error(
          `Insufficient WPOL balance. You have ${wpolBalanceValue.toFixed(4)} WPOL but need ${amount} WPOL`
        );
      }

      // Call the withdraw function
      const hash = await userWalletClient.value.writeContract({
        address: WPOL_CONTRACT_ADDRESS,
        abi: WPOL_ABI,
        chain: polygon,
        functionName: "withdraw" as const,
        args: [parseEther(amount.toString())],
        account: walletAddress.value as `0x${string}`,
      });

      // Wait for transaction confirmation
      await polygonReader.value.waitForTransactionReceipt({ hash });

      console.log(`Unwrap successful. Tx hash: ${hash}`);

      // Refresh balances
      await getPolBalance();
      await getWpolBalance();

      return hash;
    } catch (err: any) {
      error.value = err.message;
      console.error("Unwrap error:", err);
      throw err;
    }
  };

  // Invalidate cache
  const invalidateAllowanceCache = () => {
    allowanceCache.value = null;
  };

  // Connect with JWT
  const connectWithJwt = async (jwt: string) => {
    if (!web3auth.value) {
      throw new Error("Web3Auth not initialized");
    }

    try {
      error.value = null;
      console.log("Connecting with JWT...");

      await web3auth.value.connectTo(WALLET_CONNECTORS.AUTH, {
        authConnection: AUTH_CONNECTION.CUSTOM,
        authConnectionId: "playon-v1",
        idToken: jwt,
        extraLoginOptions: {
          isUserIdCaseSensitive: false,
        },
      });

      initializeClients();
      const userInfo = await getUserInfo();

      console.log("JWT connection successful");
      return userInfo;
    } catch (err: any) {
      error.value = err.message;
      console.error("JWT connection error:", err);
      throw err;
    }
  };

  const connectWithEmail = async (email: string) => {
    const authVersion = await auctionApi.auth.email.authVersion.query({ email });

    if (authVersion.version === "v1") {
      // Use legacy network
      await connectWithEmailOTP_LEGACY(email);
    } else {
      // Use new network
      await connectWithEmailOTP(email);
    }
  };

  // Connect with Email OTP
  const connectWithEmailOTP = async (email: string) => {
    if (!web3auth.value) {
      throw new Error("Web3Auth not initialized");
    }

    try {
      error.value = null;

      // Switch network if needed
      await switchOnNetwork(WEB3AUTH_NETWORK.SAPPHIRE_MAINNET);

      if (web3auth.value.status === "connected") {
        await web3auth.value.logout();
      }

      const originalLocation = window.location.origin;

      await web3auth.value.connectTo(WALLET_CONNECTORS.AUTH, {
        mfaLevel: "none",
        authConnection: AUTH_CONNECTION.EMAIL_PASSWORDLESS,
        authConnectionId: "playon-email",
        groupedAuthConnectionId: "playon-app-v1",
        extraLoginOptions: {
          login_hint: email,
        },
      });

      return { userInfo: userInfo.value };
    } catch (err: any) {
      error.value = err.message;
      console.error("Email OTP connection error:", err);
      throw err;
    }
  };

  const switchOnNetwork = async (network: WEB3AUTH_NETWORK_TYPE) => {
    let hasSwitched = false;
    if (network === WEB3AUTH_NETWORK.CYAN) {
      if (isOnLegacyNetwork.value) {
        console.log("Already on legacy network, no switch needed", web3AuthNetwork.value);
      } else {
        console.log(`Switching to v1 network(${network})`, web3AuthNetwork.value);
        setAuthVersion("v1");
        await reboot();
        hasSwitched = true;
      }
    } else if (network === WEB3AUTH_NETWORK.SAPPHIRE_MAINNET) {
      if (!isOnLegacyNetwork.value) {
        console.log("Already on new network, no switch needed", web3AuthNetwork.value);
      } else {
        console.log(`Switching to v2 network(${network}) for Google OAuth`, web3AuthNetwork.value);
        setAuthVersion("v2");
        await reboot();
        hasSwitched = true;
      }
    } else {
      console.log(
        `❌ Wrong network switch request: current=${web3AuthNetwork.value}, requested=${network}`
      );
    }
    return { hasSwitched, currentNetwork: web3AuthNetwork.value, requestedNetwork: network };
  };

  // Connect with Google OAuth
  const connectWithGoogle = async () => {
    if (!web3auth.value) {
      throw new Error("Web3Auth not initialized");
    }

    try {
      error.value = null;

      // Switch network if needed
      await switchOnNetwork(WEB3AUTH_NETWORK.SAPPHIRE_MAINNET);

      console.log("web3AuthNetwork", web3AuthNetwork);
      console.log("AUTH_CONNECTION.GOOGLE", AUTH_CONNECTION.GOOGLE);

      const job = await web3auth.value.connectTo(WALLET_CONNECTORS.AUTH, {
        authConnection: AUTH_CONNECTION.GOOGLE,
        authConnectionId: "playon-google",
        groupedAuthConnectionId: "playon-app-v1",
      });

      console.log("Google OAuth connection successful", job);

      return { userInfo: userInfo.value };
    } catch (err: any) {
      error.value = err.message;
      console.error("Google OAuth connection error:", err);
      throw err;
    }
  };

  // STILL IN WORK: Connect with Metamask
  const connectWithMetamask = async () => {
    if (!web3auth.value) {
      throw new Error("Web3Auth not initialized");
    }

    try {
      // Switch network if needed
      await switchOnNetwork(WEB3AUTH_NETWORK.SAPPHIRE_MAINNET);
      error.value = null;
      console.log("Connecting with Metamask...");

      await web3auth.value.connectTo(WALLET_CONNECTORS.METAMASK);

      initializeClients();

      return userInfo;
    } catch (err: any) {
      error.value = err.message;
      console.error("JWT connection error:", err);
      throw err;
    }
  };

  const sendERC20 = async (
    tokenConfig: {
      address: Address;
      abi: any;
      decimals: number;
      symbol: string;
      getBalance: () => Promise<any>;
      balanceValue: Ref<string>;
    },
    toAddress: string,
    amount: number,
    checkGas: boolean = false
  ): Promise<string> => {
    if (!userWalletClient.value || !walletAddress.value || !polygonReader.value) {
      throw new Error("Clients not available");
    }

    // Validate address
    if (!toAddress.match(/^0x[a-fA-F0-9]{40}$/)) {
      throw new Error("Invalid recipient address format");
    }

    // Check token balance
    await tokenConfig.getBalance();
    const balance = parseFloat(tokenConfig.balanceValue.value);
    console.log("usdcRawBalanceSmartWallet:", usdcRawBalance.value);
    console.log(
      `Sending ${amount} ${tokenConfig.symbol} to ${toAddress}. Current balance: ${balance}`
    );

    if (tokenConfig.symbol === "USDC") {
      const rawBalance = BigInt(usdcRawBalance.value);
      const rawAmount = parseUnits(amount.toString(), tokenConfig.decimals);
      console.log("rawBalance:", rawBalance.toString());
      console.log("rawAmount:", rawAmount.toString());
    }

    if (balance < amount) {
      throw new Error(
        `Insufficient ${tokenConfig.symbol} balance. You have ${balance.toFixed(4)} but need ${amount}`
      );
    }

    // Optional: Check POL for gas
    if (checkGas) {
      await getPolBalance();
      const gasEstimate = await polygonReader.value.estimateContractGas({
        address: tokenConfig.address,
        abi: tokenConfig.abi,
        functionName: "transfer",
        args: [toAddress as Address, parseUnits(amount.toString(), tokenConfig.decimals)],
        account: walletAddress.value as Address,
      });

      const gasPrice = await getGasPrice();
      const gasFee = parseFloat(formatEther(gasPrice.maxFeePerGas * gasEstimate));
      const polBalanceValue = parseFloat(polBalance.value);

      if (polBalanceValue < gasFee) {
        throw new Error(
          `Insufficient POL for gas. Need ${gasFee.toFixed(4)} POL but only have ${polBalanceValue.toFixed(4)}`
        );
      }
    }

    // Execute transfer
    const hash = await userWalletClient.value.writeContract({
      address: tokenConfig.address,
      abi: tokenConfig.abi,
      chain: polygon,
      functionName: "transfer",
      args: [toAddress as Address, parseUnits(amount.toString(), tokenConfig.decimals)],
      account: walletAddress.value as `0x${string}`,
    });

    await polygonReader.value.waitForTransactionReceipt({ hash });

    // Refresh balances
    await tokenConfig.getBalance();
    if (checkGas) await getPolBalance();

    return hash;
  };

  // 🆕 Send POL to another wallet address
  const sendPol = async ({
    toAddress,
    amount,
  }: {
    toAddress: string;
    amount: number;
  }): Promise<string> => {
    if (!userWalletClient.value || !walletAddress.value || !polygonReader.value) {
      throw new Error("Clients not available");
    }

    try {
      console.log(`Sending ${amount} POL to ${toAddress}`);

      // Validate address format
      if (!toAddress.match(/^0x[a-fA-F0-9]{40}$/)) {
        throw new Error("Invalid recipient address format");
      }

      // Check if user has enough POL balance
      await getPolBalance();
      const polBalanceValue = parseFloat(polBalance.value);

      // Get estimated gas fee
      const gasPrice = await getGasPrice();
      const estimatedGas = 21000n; // Standard gas for ETH/POL transfer
      const gasFee = parseFloat(formatEther(gasPrice.maxFeePerGas * estimatedGas));

      // Check if user has enough balance including gas
      const totalRequired = amount + gasFee;
      if (polBalanceValue < totalRequired) {
        throw new Error(
          `Insufficient POL balance. You have ${polBalanceValue.toFixed(4)} POL but need ${totalRequired.toFixed(4)} POL (${amount} POL + ${gasFee.toFixed(4)} POL gas fee)`
        );
      }

      // Send the transaction
      const hash = await userWalletClient.value.sendTransaction({
        to: toAddress as Address,
        value: parseEther(amount.toString()),
        account: walletAddress.value as `0x${string}`,
        chain: polygon,
      });

      // Wait for transaction confirmation
      await polygonReader.value.waitForTransactionReceipt({ hash });

      console.log(`POL transfer successful. Tx hash: ${hash}`);

      // Refresh balance
      await getPolBalance();

      return hash;
    } catch (err: any) {
      error.value = err.message;
      console.error("Send POL error:", err);
      throw err;
    }
  };

  const sendWpol = (toAddress: string, amount: number) =>
    sendERC20(
      {
        address: WPOL_CONTRACT_ADDRESS,
        abi: WPOL_ABI,
        decimals: 18,
        symbol: "WPOL",
        getBalance: getWpolBalance,
        balanceValue: wpolBalance,
      },
      toAddress,
      amount,
      true // Check gas
    );

  const transferCrypto = (input: { toAddress: string; amount: number; tokenSymbol: Currency }) => {
    const { toAddress, amount, tokenSymbol } = input;
    console.log(`Transferring ${amount} ${tokenSymbol} to ${toAddress}`);
    if (tokenSymbol === Currency.WPOL) {
      return sendWpol(toAddress, amount);
    } else if (tokenSymbol === Currency.USDC) {
      return sendUsdc(toAddress, amount);
    } else if (tokenSymbol === Currency.POL) {
      return sendPol({ toAddress, amount });
    } else {
      throw new Error(`Unsupported token for transfer: ${tokenSymbol}`);
    }
  };

  const sendUsdc = (toAddress: string, amount: number) =>
    sendERC20(
      {
        address: USDC_CONTRACT_ADDRESS,
        abi: USDC_ABI,
        decimals: 6,
        symbol: "USDC",
        getBalance: getUsdcBalance,
        balanceValue: usdcBalance,
      },
      toAddress,
      amount,
      false // Don't check gas (let it fail naturally)
    );

  // Unified swap function for POL <-> WPOL
  const swapPolWpol = async (
    amount: number,
    direction: "wrap" | "unwrap" | "pol-to-wpol" | "wpol-to-pol"
  ): Promise<string> => {
    // Normalize direction
    const shouldWrap = direction === "wrap" || direction === "pol-to-wpol";

    if (shouldWrap) return wrapPol(amount);
    else return unwrapWpol(amount);
  };

  // Get quote for POL -> USDC
  const getSwapQuote = async (input: {
    amountIn: number;
    tokenIn: Address;
    tokenOut: Address;
    decimalsIn: number;
    decimalsOut: number;
  }): Promise<{ amountOut: string; amountOutNumber: number; priceImpact?: number }> => {
    if (!polygonReader.value) {
      throw new Error("Reader not available");
    }

    try {
      const { amountIn, tokenIn, tokenOut, decimalsIn, decimalsOut } = input;
      const amountInWei = parseUnits(amountIn.toString(), decimalsIn);

      const WPOL_ADDRESS: `0x${string}` = "0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270";
      const BRIDGED_USDC_ADDRESS: `0x${string}` = "0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174"; //
      const NATIVE_USDC_ADDRESS: `0x${string}` = "0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359";
      let path = [tokenIn, BRIDGED_USDC_ADDRESS];
      console.log("getSwapQuote path initial:", path);
      // path = [WPOL_ADDRESS, BRIDGED_USDC_ADDRESS, NATIVE_USDC_ADDRESS];

      const amounts = await polygonReader.value.readContract({
        address: QUICKSWAP_ROUTER_ADDRESS,
        abi: QUICKSWAP_ROUTER_ABI,
        functionName: "getAmountsOut",
        args: [amountInWei, path],
      });

      const amountOut = formatUnits((amounts as bigint[])[1], decimalsOut);
      return {
        amountOut,
        amountOutNumber: parseFloat(amountOut),
      };
    } catch (err: any) {
      error.value = err.message;
      throw err;
    }
  };

  /**
   * Récupère le meilleur taux via ParaSwap
   */

  interface ParaSwapQuote {
    priceRoute: any;
    srcAmount: string;
    destAmount: string;
    gasCost: string;
  }

  interface ParaSwapSwapParams {
    amountInPOL: string;
    slippagePercent: number;
    userAddress: string;
  }

  const buildParaSwapTx = async (params: {
    amountInPOL: string;
    slippagePercent: number;
    userAddress: string;
    priceRoute: ParaSwapQuote;
  }) => {
    // Étape 2: Construit la transaction
    console.log("2️⃣ Building transaction...");

    const slippage = params.slippagePercent * 100; // ParaSwap utilise basis points (1% = 100)
    const amountInWei = ethers.utils.parseEther(params.amountInPOL).toString();

    const txParams = {
      srcToken: "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE", // POL natif
      destToken: USDC_CONTRACT_ADDRESS,
      srcAmount: amountInWei,
      // destAmount: params.priceRoute.destAmount,
      priceRoute: params.priceRoute,
      userAddress: params.userAddress,
      partner: "playon", // Ton identifiant (optionnel, pour analytics)
      slippage: slippage,
    };

    const txResponse = await axios.post(`${PARASWAP_API}/transactions/137`, txParams);
    return txResponse.data;
  };
  const getParaSwapQuote = async (
    amountInPOL: string,
    userAddress: string
  ): Promise<ParaSwapQuote> => {
    const amountInWei = parseUnits(amountInPOL.toString(), 18);

    const params = {
      srcToken: "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE",
      destToken: USDC_CONTRACT_ADDRESS,
      amount: amountInWei,
      srcDecimals: 18,
      destDecimals: 6,
      side: "SELL",
      network: 137,
      userAddress: userAddress,
    };

    try {
      const response = await axios.get(`${PARASWAP_API}/prices`, { params });

      console.log("📊 ParaSwap Quote:");
      console.log(`  Input: ${amountInPOL} POL`);
      console.log(`  Output: ${formatUnits(response.data.priceRoute.destAmount, 6)} USDC`);
      console.log(
        `  Route: ${response.data.priceRoute.bestRoute[0]?.swaps[0]?.swapExchanges[0]?.exchange || "Multi-hop"}`
      );

      console.log("response.data", response.data);

      return response.data.priceRoute;
    } catch (error: any) {
      console.error("❌ ParaSwap quote failed:", error.response?.data || error.message);
      throw error;
    }
  };

  // Helper spécifique POL → USDC (pour faciliter l'usage)
  const getSwapQuotePolToUsdc = async (
    amountPol: number
  ): Promise<{
    amountOut: string;
    amountOutNumber: number;
    pricePerUsdc: number;
    pricePerPol: number;
  }> => {
    const quote = await getParaSwapQuote(amountPol.toString(), walletAddress.value!);
    const expectedUSDC = +ethers.utils.formatUnits(quote.destAmount, 6);

    const pricePerPol = expectedUSDC / amountPol; // 1 POL = X USDC
    const pricePerUsdc = amountPol / expectedUSDC; // 1 USDC = X POL
    return {
      amountOut: `${expectedUSDC}`,
      amountOutNumber: expectedUSDC,
      pricePerUsdc,
      pricePerPol,
    };
  };

  // Swap POL natif vers USDC (direct, sans wrap)
  const swapPolToUsdc = async (
    amountPol: number,
    slippageTolerance: number = 0.5
  ): Promise<string> => {
    if (!userWalletClient.value || !walletAddress.value || !polygonReader.value) {
      throw new Error("Clients not available");
    }

    try {
      console.log(`Swapping ${amountPol} POL to USDC...`);

      const resp = await getParaSwapQuote(amountPol.toString(), walletAddress.value!);
      const txData = await buildParaSwapTx({
        amountInPOL: amountPol.toString(),
        slippagePercent: slippageTolerance,
        userAddress: walletAddress.value!,
        priceRoute: resp,
      });

      // Vérifier le balance POL
      await getPolBalance();
      const polBalanceValue = parseFloat(polBalance.value);

      // Estimer le gas
      const gasEstimate = await estimateSwapPolToUsdcGas(amountPol);
      const totalRequired = amountPol + parseFloat(gasEstimate.totalGasFee);

      if (polBalanceValue < totalRequired) {
        throw new Error(
          `Insufficient POL balance. You have ${polBalanceValue.toFixed(4)} POL but need ${totalRequired.toFixed(4)} POL (${amountPol} POL + ${gasEstimate.totalGasFee} POL gas fee)`
        );
      }

      // Get quote
      const quote = await getSwapQuotePolToUsdc(amountPol);
      console.log(`Expected USDC output: ${quote.amountOut}`);

      const swapHash = await userWalletClient.value.sendTransaction({
        to: txData.to as Address,
        data: txData.data as `0x${string}`,
        value: BigInt(txData.value),
        gas: BigInt(txData.gas || 500000),
        chain: polygon,
        account: walletAddress!.value as `0x${string}`,
      });

      await polygonReader.value.waitForTransactionReceipt({ hash: swapHash });

      console.log(`Swap successful! Tx hash: ${swapHash}`);

      // Refresh balances
      await getPolBalance();
      await getUsdcBalance();

      return swapHash;
    } catch (err: any) {
      error.value = err.message;
      console.error("Swap POL to USDC error:", err);
      throw err;
    }
  };

  // Estimate gas
  const estimateSwapPolToUsdcGas = async (
    amountPol: number
  ): Promise<{
    swapGas: bigint;
    totalGasFee: string;
  }> => {
    if (!polygonReader.value || !walletAddress.value) {
      throw new Error("Clients not available");
    }

    try {
      const amountIn = parseEther(amountPol.toString());
      const path = [WPOL_CONTRACT_ADDRESS, USDC_CONTRACT_ADDRESS];
      const deadline = BigInt(Math.floor(Date.now() / 1000) + 60 * 20);

      const swapGas = await polygonReader.value.estimateContractGas({
        address: QUICKSWAP_ROUTER_ADDRESS,
        abi: QUICKSWAP_ROUTER_ABI,
        functionName: "swapExactETHForTokens",
        args: [0n, path, walletAddress.value as Address, deadline],
        account: walletAddress.value as Address,
        value: amountIn,
      });

      const gasFee = await calculateGasFee(swapGas, 120);

      return {
        swapGas,
        totalGasFee: formatEther(gasFee),
      };
    } catch (err: any) {
      error.value = err.message;
      throw err;
    }
  };

  const verifyWallet = async () => {
    if (!web3auth.value) {
      throw new Error("Web3Auth not initialized");
    }
    if (!walletAddress.value) await getWalletAddress();

    try {
      console.log("🔐 Verifying wallet ownership for", walletAddress.value);
      const domain = window.location.hostname;
      const challenge = await auctionApi.auth.wallet.challenge.query({
        walletAddress: walletAddress.value!,
        domain,
      });
      // 3. Create SIWE message
      const siweMessage = new SiweMessage({
        domain: domain,
        address: walletAddress.value,
        statement: `Sign in with Polygon to ${challenge.domain}.`,
        uri: window.location.origin,
        version: "1",
        chainId: polygon.id,
        nonce: challenge.nonce,
        issuedAt: challenge.issuedAt,
      });

      const message = siweMessage.prepareMessage();
      const signature = await signMessage({
        message,
        walletAddress: walletAddress.value! as `0x${string}`,
      });
      if (!signature) throw new Error("Message signing failed");

      console.log("✅ Wallet verification successful");

      return {
        walletAddress: walletAddress.value!,
        message,
        signature,
      };
    } catch (err: any) {
      error.value = err.message;
      console.log(`❌ Failed to Verify wallet ownership for ${walletAddress.value}`, err);
      throw err;
    }
  };

  const connectWithEmailOTP_LEGACY = async (email: string) => {
    if (!web3auth.value) {
      throw new Error("Web3Auth not initialized. Call initialize() first.");
    }

    try {
      // Clear previous errors
      error.value = null;

      // Switch network if needed
      await switchOnNetwork(WEB3AUTH_NETWORK.CYAN);

      console.log("web3AuthNetwork.value", web3AuthNetwork.value);
      console.log("isOnLegacyNetwork.value", isOnLegacyNetwork.value);
      // If not on legacy network, switch to v1 and reboot
      if (!isOnLegacyNetwork.value) {
        console.log("Switching to auth v1 for Email OTP Legacy");
        setAuthVersion("v1");
        await reboot();
        console.log("Switched to legacy network:", web3AuthNetwork.value);
      }

      // Logout first if already connected
      if (web3auth.value.status === "connected") {
        await web3auth.value.logout();
      }

      // Set redirect after login
      sessionStorage.setItem("postLoginRedirect", "marketplace");

      // Connect using Email OTP
      await web3auth.value.connectTo(WALLET_CONNECTORS.AUTH, {
        mfaLevel: "none",
        authConnection: AUTH_CONNECTION.EMAIL_PASSWORDLESS,
        extraLoginOptions: {
          login_hint: email,
        },
      });
    } catch (err: any) {
      error.value = err.message;
      console.error("ConnectTo error:", err);
      throw err;
    } finally {
    }
  };

  // Get user info
  const getUserInfo = async () => {
    if (!web3auth.value) {
      throw new Error("Web3Auth not initialized");
    }

    try {
      error.value = null;
      const info = await web3auth.value.getUserInfo();
      userInfo.value = info;

      return info;
    } catch (err: any) {
      error.value = err.message;
      console.error("Get user info error:", err);
      throw err;
    }
  };

  const getSmartWalletEthersSigner = (client: any) => {
    const { chain, transport } = client;

    // Create a provider that understands the viem transport
    const provider = new providers.Web3Provider(transport, {
      chainId: chain.id,
      name: chain.name,
    });

    // Get the signer for the Smart Wallet address
    return provider.getSigner(client.account.address);
  };

  const initSmartWallet = async () => {
    console.log("🏛️ Initializing Smart Wallet...");
    // SMART CONTRACTS SETUP
    const policyId = import.meta.env.VITE_ALCHEMY_POLICY_ID!;
    const wallet = userWalletClient.value;

    // web3auth It's an arbitrary name to identify the signer type.
    const signer = new WalletClientSigner(wallet, "web3auth");

    const gameSalt = hexToBigInt(keccak256(encodePacked(["string"], [FUNGIBALL_V1])));

    smartWalletClient.value = await createModularAccountV2Client({
      // accountAddress: acount.address,
      chain: polygonAlchemy,
      transport: alchemy({
        apiKey: import.meta.env.VITE_ALCHEMY_KEY!,
      }),
      signer: signer,
      policyId,
      salt: gameSalt,
    });

    console.log("smartWalletAddress", smartWalletClient.value.getAddress());
    console.log("✅ Smart Wallet initialized");
  };

  return {
    // State
    status,
    signer: ethersSigner,
    web3auth,
    userInfo,
    isConnected,
    isConnecting,
    isInitialized,
    isOnLegacyNetwork,
    error,
    polBalance,
    polRawBalance,
    wpolBalance,
    wpolRawBalance,
    usdcBalance,
    usdcRawBalance,
    usdcBalanceSmartWallet,
    usdcRawBalanceSmartWallet,
    walletAddress,
    seaportService,
    seaportSmartWalletService,
    polygonReader,
    userWalletClient,
    web3AuthNetwork,
    connectionType,

    // Smart Wallet
    smartWalletClient,
    smartWalletAddress,
    initSmartWallet,

    // Auth
    initialize,
    reboot,
    connectWithJwt,
    connectWithMetamask,
    connectWithEmail,
    connectWithEmailOTP,
    connectWithGoogle,
    connectWithEmailOTP_LEGACY,
    logout,
    switchOnNetwork,

    // User Wallet Info
    getUserInfo,
    verifyWallet,

    // Allowance
    getAllowance,
    ensureAllowance,
    invalidateAllowanceCache,

    // Gas Info
    getGasPrice,
    estimateContractGas,
    calculateGasFee,
    hasEnoughGas,

    // Wallet Balance
    getPolBalance,
    getWpolBalance,
    getUsdcBalance,

    // Transfers
    sendPol,
    sendWpol,
    sendUsdc,
    transferCrypto,
    estimateWpolTransferGas,
    estimatePolTransferGas,

    // Swaps
    estimateWrapUnWrapGasFast,
    wrapPol,
    unwrapWpol,
    swapPolWpol,
    estimateSwapPolToUsdcGas,
    getSwapQuotePolToUsdc,
    swapPolToUsdc,
  };
});
